import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useRelatedOrders, useRelatedOrdersInvalidate } from '@/hooks/api/0640_related_orders';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { RelatedOrder } from '@/hooks/api/models';
import { useDestroyRelatedOrder } from '@/hooks/api/0660_destroy_related_order';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { useUpdateRelatedOrder } from '@/hooks/api/0651_update_related_order';
import { toastApiError } from '@/lib/toast';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { InputRatio } from '@/components/element/Form/InputRatio';
import { SalesOrderOutlineLogic } from '@/hooks/api/models/logics';

export const RelatedProjectsElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { data: relatedOrders } = useRelatedOrders({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const [delTarget, setDelTarget] = useState<RelatedOrder>();
  const { mutate, isSuccess, isPending } = useDestroyRelatedOrder();
  const { invalidate } = useRelatedOrdersInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });

  useEffect(() => {
    if (isSuccess) invalidate();
  }, [isSuccess]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onDeleteClick = () => {
    setShowLoading(true);
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, purchase_order_id: _po_!.id, id: delTarget!.id });
  };

  if (relatedOrders == null) return <></>;
  if (relatedOrders.length == 0) return <></>;

  return (
    <div className="table-responsive">
      <ModalConfirm modalId={`delConfirm`} okClicked={() => onDeleteClick()} message={'削除します。よろしいですか？'} />

      <table className="table align-items-center">
        <thead>
          <tr>
            <th className="text-secondary text-xxs font-weight-bolder opacity-7 px-2">関連するプロジェクト</th>
            <th className="text-secondary text-xxs font-weight-bold opacity-7 px-2 text-end" style={{ width: 150 }}>
              按分比率（％）
            </th>
            <th className="text-secondary text-xxs font-weight-bold opacity-7 px-2" style={{ width: 100 }}></th>
          </tr>
        </thead>
        <tbody>
          {relatedOrders.map((d) => (
            <RelatedOrderRow key={`ro-${d.id}`} d={d} setDelTarget={setDelTarget} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  id: number;
  allocation_ratio: number;
};

const RelatedOrderRow = ({
  d,
  setDelTarget,
}: {
  d: RelatedOrder;
  setDelTarget: Dispatch<SetStateAction<RelatedOrder | undefined>>;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, error, isPending } = useUpdateRelatedOrder();
  const { invalidate } = useRelatedOrdersInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      id: d.id,
      allocation_ratio: d.allocation_ratio,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const relatedOrderLabel = useMemo(() => {
    return SalesOrderOutlineLogic.toLabel(d.sales_order_outline);
  }, [d]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      setEditing(false);
    }
  }, [data]);

  if (!editing) {
    return (
      <tr key={`ro-${d.id}`}>
        <td className="text-wrap font-weight-bold text-sm text-dark mb-1">{relatedOrderLabel}</td>
        <td className="text-end text-dark text-xs">{d.allocation_ratio == 0 ? '-' : `${d.allocation_ratio}%`}</td>
        <td className="text-end">
          <button className="btn btn-icon-only bg-gradient-success me-1" onClick={() => setEditing((e) => !e)}>
            <i className="fas fa-edit"></i>
          </button>
          <button
            className="btn btn-icon-only bg-gradient-danger"
            data-bs-toggle="modal"
            data-bs-target={`#delConfirm`}
            onClick={() => setDelTarget((_) => d)}
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr key={`ro-${d.id}`}>
      <td className="text-wrap font-weight-bold text-sm text-dark mb-1">{d.sales_order_outline.name}</td>
      <td className="text-end text-dark text-xs">
        <Controller
          render={({ field: { onChange, value, ...field } }) => <InputRatio onChange={onChange} value={value} />}
          control={control}
          name="allocation_ratio"
        />
      </td>
      <td className="text-end">
        <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing((e) => !e)}>
          <i className="fas fa-undo"></i>
        </button>
        <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
          <i className="fas fa-check-circle"></i>
        </button>
      </td>
    </tr>
  );
};
